import { useSearchParams } from 'react-router-dom';
import { useDrawerStore } from '../../../store';
import { useFetchData } from '../../../hooks';
import {
  BaseModel,
  FormField,
  MaterialIssuanceFilter,
  PageableResponse,
} from '../../../types';
import { MaterialIssuanceType, Resource } from '../../../lib/enums';
import React, { useState } from 'react';
import { Button, Drawer, Form, Space } from 'antd';
import PrimaryButton from '../../../components/ui/PrimaryButton';
import { DownloadOutlined, FilterOutlined } from '@ant-design/icons';
import CustomForm from '../../../components/ui/CustomForm';
import SubmitButton from '../../../components/ui/SubmitButton';
import { dateFormat } from '../../../lib/utils';
import dayjs from 'dayjs';

const MaterialIssuanceHeaderFilter = ({
  startDate,
  endDate,
  userLocation,
  type,
  data,
  exportReport,
}: {
  startDate: string | null;
  endDate: string | null;
  type: string | undefined | null;
  userLocation: string | undefined;
  data?: any | null;
  exportReport: () => void;
}) => {
  const [form] = Form.useForm();

  const drawerStore = useDrawerStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: defaultLocationData, isLoading: isDefaultLocationLoading } =
    useFetchData<PageableResponse<BaseModel>>('/location/locations', {
      resource: Resource.MIN,
      search: '',
      length: '999999',
    });
  const [filter, setFilter] = useState<MaterialIssuanceFilter>({
    localtion_id:
      searchParams.get('locationId') !== undefined &&
      searchParams.get('locationId') !== null
        ? searchParams.get('locationId')
        : userLocation,
    startDate: startDate,
    endDate: endDate,
    datepicker: [startDate, endDate],
    type: type,
  });

  const typeOptions: BaseModel[] = Object.keys(MaterialIssuanceType).map(
    (key) =>
      ({
        id: key,
        code: MaterialIssuanceType[key as keyof typeof MaterialIssuanceType],
        description:
          MaterialIssuanceType[key as keyof typeof MaterialIssuanceType],
      }) as BaseModel,
  );

  const onFinish = (values: {
    localtion_id: string;
    datepicker: string;
    material: string;
    proder: string;
    sku: string;
    type: string;
  }) => {
    setSearchParams((prev) => {
      prev.set('startDate', dayjs(values.datepicker[0]).format(dateFormat));
      prev.set('endDate', dayjs(values.datepicker[1]).format(dateFormat));
      prev.set('locationId', values.localtion_id);
      prev.set('type', values.type);
      prev.set('proder', values.proder === undefined ? '' : values.proder);
      prev.set('sku', values.sku === undefined ? '' : values.sku);
      prev.set(
        'material',
        values.material === undefined ? '' : values.material,
      );
      return prev;
    });
    setFilter({
      localtion_id: values.localtion_id,
      startDate: dayjs(values.datepicker[0]).format(dateFormat),
      endDate: dayjs(values.datepicker[1]).format(dateFormat),
      datepicker: [
        dayjs(values.datepicker[0]).format(dateFormat),
        dayjs(values.datepicker[1]).format(dateFormat),
      ],
      type: values.type,
      material: values.material,
      proder: values.proder,
      sku: values.sku,
    });
  };

  const formFields: FormField[] = [
    {
      name: 'datepicker',
      label: 'Date coverage',
      rules: [{ required: true, message: 'Please select date coverage' }],
      type: 'date-range-picker',
      allowClear: false,
    },
    {
      name: 'localtion_id',
      label: 'Default Location',
      rules: [{ required: true, message: 'Please select default location' }],
      values: defaultLocationData?.data,
      isMultiple: false,
      isLoading: isDefaultLocationLoading,
      type: 'select',
      allowClear: false,
    },
    {
      name: 'type',
      label: 'Type',
      allowClear: false,
      type: 'select',
      isLoading: false,
      isMultiple: false,
      values: typeOptions,
      rules: [{ required: true, message: 'Please select type' }],
    },
    {
      name: 'sku',
      label: 'SKU',
      type: 'input',
      allowClear: true,
    },
    {
      name: 'proder',
      label: 'Proder',
      type: 'input',
      allowClear: true,
    },
    {
      name: 'material',
      label: 'Material',
      type: 'input',
      allowClear: true,
    },
  ];

  return (
    <>
      <div className={'flex p-4 justify-end'}>
        <Space>
          <PrimaryButton
            disable={!data}
            icon={<DownloadOutlined />}
            onClick={exportReport}
            label={'Export'}
          />
          <PrimaryButton
            icon={<FilterOutlined />}
            onClick={drawerStore.onClose}
            label={'Filter'}
          />
        </Space>
      </div>
      <Drawer
        onClose={drawerStore.onClose}
        open={drawerStore.isOpen}
        extra={
          <Space>
            <Button onClick={drawerStore.onClose}>Cancel</Button>
            <SubmitButton form={form}>Filter</SubmitButton>
          </Space>
        }
      >
        <CustomForm
          rowCol={1}
          data={filter}
          form={form}
          formFields={formFields}
          onFinish={onFinish}
          layout={'vertical'}
          formName={'form_drawer'}
        />
      </Drawer>
    </>
  );
};

export default MaterialIssuanceHeaderFilter;
